import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, CheckCircle2, Timer, Pause, Play, RotateCcw, BookOpen, Flame, Scale, Eye, EyeOff, ChevronLeft, ChevronRight, SkipForward, Zap } from "lucide-react";
import RankUpModal from "../components/RankUpModal";
import PRModal from "../components/PRModal";
import RIRConfirmModal from "../components/RIRConfirmModal";
import ExerciseDemo from "../components/ExerciseDemo";
import MuscleMap from "../components/MuscleMap";
import LastTimeChip from "../components/LastTimeChip";
import { warmupTemplatesFor, buildWarmupsFromTemplates } from "../lib/warmups";

const MAX_SETS = 4;
const REST_SECONDS = 180; // 3 minutes

// Generates a short bell tone via Web Audio API (no asset needed)
function playBell() {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.type = "sine";
        o.frequency.setValueAtTime(880, ctx.currentTime);
        g.gain.setValueAtTime(0.0001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 1.4);
        o.start(); o.stop(ctx.currentTime + 1.5);
        // Second softer chime
        const o2 = ctx.createOscillator(); const g2 = ctx.createGain();
        o2.connect(g2); g2.connect(ctx.destination);
        o2.type = "sine";
        o2.frequency.setValueAtTime(660, ctx.currentTime + 0.2);
        g2.gain.setValueAtTime(0.0001, ctx.currentTime + 0.2);
        g2.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + 0.22);
        g2.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 1.6);
        o2.start(ctx.currentTime + 0.2); o2.stop(ctx.currentTime + 1.6);
    } catch (e) { /* ignore */ }
}

// Numeric input that strips leading zeros while typing.
// `showZero` (true for RIR fields) renders 0 as "0" instead of blank, so the
// user can SEE that they've intentionally set the value to zero (i.e. "trained
// to failure" for RIR). Without it, 0 renders as empty so weight/reps fields
// start fresh.
function NumInput({ value, onChange, testId, step = 1, min, max, className = "", invalid = false, readOnly = false, showZero = false }) {
    const display = (value === 0 || value === "0") ? (showZero ? "0" : "") : String(value);
    const handle = (e) => {
        if (readOnly) return;
        const raw = e.target.value;
        if (raw === "") { onChange(0); return; }
        // Strip leading zeros (but allow "0.x")
        const cleaned = raw.replace(/^0+(?=\d)/, "");
        const num = parseFloat(cleaned);
        if (Number.isNaN(num)) { onChange(0); return; }
        onChange(num);
    };
    const errCls = invalid ? "border-accent-red ring-1 ring-accent-red/60 bg-accent-red/5" : "border-border";
    const roCls = readOnly ? "bg-secondary/30 text-muted-foreground" : "bg-background";
    return (
        <input
            type="number"
            inputMode="decimal"
            step={step}
            min={min}
            max={max}
            value={display}
            placeholder={readOnly ? "—" : "0"}
            onChange={handle}
            readOnly={readOnly}
            disabled={readOnly}
            data-testid={testId}
            data-invalid={invalid ? "true" : undefined}
            aria-invalid={invalid || undefined}
            className={`w-20 rounded-sm border px-2 py-1 ${errCls} ${roCls} ${className}`}
        />
    );
}

function RestTimer() {
    const [seconds, setSeconds] = useState(REST_SECONDS);
    const [running, setRunning] = useState(false);
    const intervalRef = useRef(null);

    useEffect(() => {
        if (running) {
            intervalRef.current = setInterval(() => {
                setSeconds(s => {
                    if (s <= 1) {
                        clearInterval(intervalRef.current);
                        setRunning(false);
                        playBell();
                        return 0;
                    }
                    return s - 1;
                });
            }, 1000);
            return () => clearInterval(intervalRef.current);
        }
    }, [running]);

    const start = () => {
        if (seconds === 0) setSeconds(REST_SECONDS);
        setRunning(true);
    };
    const pause = () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        setRunning(false);
    };
    const reset = () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        setRunning(false);
        setSeconds(REST_SECONDS);
    };

    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    const pct = (seconds / REST_SECONDS) * 100;

    return (
        <div className="tactical-card sticky bottom-4 z-30 p-4" data-testid="rest-timer">
            <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <Timer className="h-5 w-5 text-accent-red" />
                    <div>
                        <div className="label-eyebrow text-muted-foreground">Rest timer · 3 min</div>
                        <div className="font-display text-3xl font-black tabular-nums" data-testid="timer-display">
                            {m}:{s.toString().padStart(2, "0")}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {!running ? (
                        <button onClick={start} data-testid="timer-start" className="flex items-center gap-1 rounded-sm bg-accent-red px-3 py-2 text-xs font-bold uppercase tracking-wider text-white hover:bg-[color:var(--accent-hover)]">
                            <Play className="h-3 w-3" /> {seconds === REST_SECONDS ? "Start rest" : seconds === 0 ? "Restart" : "Resume"}
                        </button>
                    ) : (
                        <button onClick={pause} data-testid="timer-pause" className="flex items-center gap-1 rounded-sm border border-border px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-secondary">
                            <Pause className="h-3 w-3" /> Pause
                        </button>
                    )}
                    <button onClick={reset} data-testid="timer-reset" className="rounded-sm border border-border p-2 hover:bg-secondary" aria-label="Reset">
                        <RotateCcw className="h-3 w-3" />
                    </button>
                </div>
            </div>
            <div className="mt-3 h-1 w-full bg-border">
                <div className="h-full bg-accent-red transition-[width] duration-1000" style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}

export default function Session() {
    const { logId } = useParams();
    const navigate = useNavigate();
    const [log, setLog] = useState(null);
    const [imbalanceMap, setImbalanceMap] = useState({});
    const [effort, setEffort] = useState(7);
    const [rankUp, setRankUp] = useState(null);
    const [expEarned, setExpEarned] = useState(null);
    const [prsSet, setPrsSet] = useState([]);
    const [showPRs, setShowPRs] = useState(false);
    const [openDemos, setOpenDemos] = useState({});
    const [rirConfirmOpen, setRirConfirmOpen] = useState(false);
    const [rirFlags, setRirFlags] = useState({ failureSets: [], lowIntensitySets: [] });
    // ─── Focus mode ──────────────────────────────────────────────────
    // Active sessions render ONE exercise at a time for maximum focus.
    // `focusIdx` is the index currently on-screen; `slideDir` drives the
    // CSS slide animation (next = slide-in from right, prev = from left).
    // `punchKey` re-keys the active CTA to replay the punch-ring animation
    // on every press. `shakeKey` does the same for the shake-on-error.
    const [focusIdx, setFocusIdx] = useState(0);
    const [slideDir, setSlideDir] = useState("next");
    const [punchKey, setPunchKey] = useState(0);
    const [shakeKey, setShakeKey] = useState(0);
    const [invalidFields, setInvalidFields] = useState(() => new Set()); // "exIdx-setIdx-field"
    const [autoSaveState, setAutoSaveState] = useState("idle"); // "idle" | "saving" | "saved"
    const dirtyRef = useRef(false);
    const saveTimerRef = useRef(null);

    // True when the log was already finished — page enters read-only mode
    // (history view) so the user can review prior sets without changing them.
    const isReadOnly = !!log?.finished_at;

    // ─── Screen Wake Lock ──────────────────────────────────────────────
    // Users training with the phone on a rack, bench, or ground get their
    // screen dimmed / locked every 30-60s by the OS. Two consequences:
    //   1. The rest timer visibly pauses (JS setInterval keeps running but
    //      the tab throttles heavy work in background mode on some browsers).
    //   2. They have to tap the screen every rest cycle to see the timer.
    // The Screen Wake Lock API lets a webapp declare "keep this screen on
    // while I'm doing something important". Supported in Chrome/Edge/Safari
    // 16.4+ / Android Chrome / Samsung Internet. Silently no-ops on
    // unsupported browsers — the app doesn't break, just doesn't get the
    // benefit.
    //
    // We acquire on mount for ACTIVE (non-read-only) sessions, release on
    // unmount, and re-acquire on visibilitychange because most browsers
    // drop the lock the moment the tab is hidden (switching apps, incoming
    // call, notification pull-down, etc.) — the visibility handler
    // reclaims it the moment the user comes back.
    useEffect(() => {
        if (isReadOnly) return;                    // history view — no need to keep screen on
        if (!("wakeLock" in navigator)) return;    // older browser — silent no-op
        let sentinel = null;
        let cancelled = false;

        const acquire = async () => {
            try {
                const s = await navigator.wakeLock.request("screen");
                // Effect may have torn down while the async request was in
                // flight (React 18 StrictMode dev double-invoke, or a fast
                // SPA nav away). If we're cancelled by the time the promise
                // resolves, release the fresh sentinel immediately rather
                // than orphaning it in the OS-level list.
                if (cancelled) { s.release().catch(() => {}); return; }
                sentinel = s;
                sentinel.addEventListener("release", () => { sentinel = null; });
            } catch { /* denied — nothing we can do, silent */ }
        };

        const onVisibility = () => {
            if (cancelled) return;
            if (document.visibilityState === "visible" && !sentinel) {
                acquire();
            }
        };

        acquire();
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            cancelled = true;
            document.removeEventListener("visibilitychange", onVisibility);
            if (sentinel) {
                sentinel.release().catch(() => {});
                sentinel = null;
            }
        };
    }, [isReadOnly]);
    const toggleDemo = (exIdx) => setOpenDemos((p) => ({ ...p, [exIdx]: !p[exIdx] }));

    const markDirty = useCallback(() => {
        dirtyRef.current = true;
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        setAutoSaveState("saving");
    }, []);

    const clearInvalid = useCallback((exIdx, setIdx, field) => {
        const k = `${exIdx}-${setIdx}-${field}`;
        setInvalidFields((prev) => {
            if (!prev.has(k)) return prev;
            const n = new Set(prev);
            n.delete(k);
            return n;
        });
    }, []);

    useEffect(() => {
        api.get(`/workouts/${logId}`).then(r => {
            const data = r.data;
            // Initialize warmup_sets for each exercise based on its position in the day
            if (Array.isArray(data?.exercises)) {
                data.exercises = data.exercises.map((ex, idx) => {
                    const templates = warmupTemplatesFor(data.exercises, idx);
                    if (!Array.isArray(ex.warmup_sets) || ex.warmup_sets.length !== templates.length) {
                        ex = {
                            ...ex,
                            warmup_sets: buildWarmupsFromTemplates(templates, ex.sets?.[0]?.weight ?? 0),
                        };
                    }
                    return ex;
                });
            }
            setLog(data);
            // When viewing a finished workout, mirror the persisted perceived
            // effort so the slider shows what was logged (it's locked anyway).
            if (data?.finished_at && typeof data?.perceived_effort === "number") {
                setEffort(data.perceived_effort);
            }
            // Resume-to-current-exercise: on cold load, jump focusIdx to the
            // first exercise the user hasn't finished yet. An exercise is
            // "done" when every working set has weight > 0 AND reps > 0
            // (matching validateSets()); optional exercises that are ENTIRELY
            // blank are treated as intentionally skipped and count as done.
            // Fresh logs land on 0 (all blank → first). Finished logs land
            // on 0 (review mode). Skips forward to where the user actually
            // left off after tabbing away.
            if (!data?.finished_at && Array.isArray(data?.exercises)) {
                const isSetLogged = (ex, s) => {
                    if (Number(s?.weight) > 0 && Number(s?.reps) > 0) {
                        if (!ex.unilateral) return true;
                        return Number(s?.weight_left) > 0 && Number(s?.reps_left) > 0;
                    }
                    return false;
                };
                const isExerciseDone = (ex) => {
                    const sets = ex?.sets || [];
                    if (sets.length === 0) return true;
                    if (ex?.optional) {
                        const anyData = sets.some((s) =>
                            Number(s?.weight) > 0 || Number(s?.reps) > 0 ||
                            (ex.unilateral && (Number(s?.weight_left) > 0 || Number(s?.reps_left) > 0))
                        );
                        if (!anyData) return true;   // untouched optional → skipped
                    }
                    return sets.every((s) => isSetLogged(ex, s));
                };
                const firstIncomplete = data.exercises.findIndex((ex) => !isExerciseDone(ex));
                if (firstIncomplete > 0) setFocusIdx(firstIncomplete);
            }
        }).catch(() => navigate("/workouts"));
    }, [logId, navigate]);

    // Fetch imbalance data once on mount — used to nudge the user when a unilateral
    // exercise in this session is currently flagged (≥10% side gap).
    useEffect(() => {
        api.get("/progress/imbalance")
            .then(r => {
                const map = {};
                for (const ex of (r.data?.exercises || [])) {
                    if (ex.flagged) map[ex.exercise_id] = ex;
                }
                setImbalanceMap(map);
            })
            .catch(() => setImbalanceMap({}));
    }, []);

    // ─── Last-time history per exercise ───────────────────────────────
    // Powers the read-only "Last time" chip + 4-session sparkline above each
    // exercise's working sets. Fetched once per unique exercise_id in this
    // log, cached in state keyed by exercise_id, and never mutated by input
    // (chip stays stable even as the lifter logs today's sets). Silently
    // no-ops on failure — the chip just renders the "First time" fallback.
    const [lastTimeMap, setLastTimeMap] = useState({});
    useEffect(() => {
        if (!log || !Array.isArray(log.exercises)) return;
        const ids = Array.from(new Set(log.exercises.map((e) => e.exercise_id).filter(Boolean)));
        const missing = ids.filter((id) => !(id in lastTimeMap));
        if (missing.length === 0) return;
        let cancelled = false;
        Promise.all(
            missing.map((id) =>
                api.get(`/workouts/history/exercise-recent/${id}?limit=4`)
                    .then((r) => [id, r.data?.sessions || []])
                    .catch(() => [id, []])
            )
        ).then((pairs) => {
            if (cancelled) return;
            setLastTimeMap((prev) => {
                const next = { ...prev };
                for (const [id, sessions] of pairs) next[id] = sessions;
                return next;
            });
        });
        return () => { cancelled = true; };
    }, [log?.log_id]);

    const updateSet = (exIdx, setIdx, field, value) => {
        if (isReadOnly) return;
        const next = { ...log };
        next.exercises = [...log.exercises];
        const ex = { ...next.exercises[exIdx] };
        ex.sets = [...ex.sets];
        ex.sets[setIdx] = { ...ex.sets[setIdx], [field]: field === "notes" ? value : Number(value) };
        // Auto-fill warmup weights when the first working-set weight is entered and warmups are still blank
        if (setIdx === 0 && field === "weight" && Number(value) > 0) {
            const warmupsBlank = !ex.warmup_sets || ex.warmup_sets.every(w => !Number(w?.weight));
            if (warmupsBlank) {
                const templates = warmupTemplatesFor(log.exercises, exIdx);
                ex.warmup_sets = buildWarmupsFromTemplates(templates, Number(value));
            }
        }
        next.exercises[exIdx] = ex;
        setLog(next);
        markDirty();
        // Clear validation error for this field as soon as the user types a non-zero value
        if (Number(value) > 0) clearInvalid(exIdx, setIdx, field);
    };

    const updateWarmup = (exIdx, wIdx, weight) => {
        if (isReadOnly) return;
        const next = { ...log };
        next.exercises = [...log.exercises];
        const ex = { ...next.exercises[exIdx] };
        ex.warmup_sets = [...(ex.warmup_sets || [])];
        ex.warmup_sets[wIdx] = { ...ex.warmup_sets[wIdx], weight: Number(weight) };
        next.exercises[exIdx] = ex;
        setLog(next);
        markDirty();
    };

    const refillWarmups = (exIdx) => {
        if (isReadOnly) return;
        const ex = log.exercises[exIdx];
        const w = ex?.sets?.[0]?.weight ?? 0;
        if (!w) {
            toast.message("Enter your first working set weight first");
            return;
        }
        const templates = warmupTemplatesFor(log.exercises, exIdx);
        const next = { ...log };
        next.exercises = [...log.exercises];
        next.exercises[exIdx] = { ...ex, warmup_sets: buildWarmupsFromTemplates(templates, w) };
        setLog(next);
        markDirty();
    };

    const addSet = (exIdx) => {
        if (isReadOnly) return;
        const next = { ...log };
        next.exercises = [...log.exercises];
        const ex = { ...next.exercises[exIdx] };
        if (ex.sets.length >= MAX_SETS) {
            toast.warning(`Max ${MAX_SETS} sets per exercise`);
            return;
        }
        const newSet = { set_number: ex.sets.length + 1, reps: 0, weight: 0, rir: 1 };
        if (ex.unilateral) {
            newSet.weight_left = 0;
            newSet.reps_left = 0;
            newSet.rir_left = 1;
        }
        ex.sets = [...ex.sets, newSet];
        next.exercises[exIdx] = ex;
        setLog(next);
        markDirty();
    };

    const removeSet = (exIdx, setIdx) => {
        if (isReadOnly) return;
        const next = { ...log };
        next.exercises = [...log.exercises];
        const ex = { ...next.exercises[exIdx] };
        if (ex.sets.length <= 1) {
            toast.warning("Need at least 1 set");
            return;
        }
        ex.sets = ex.sets.filter((_, i) => i !== setIdx).map((s, i) => ({ ...s, set_number: i + 1 }));
        next.exercises[exIdx] = ex;
        setLog(next);
        markDirty();
        // Drop any validation flags pointing at the removed set
        setInvalidFields((prev) => {
            if (prev.size === 0) return prev;
            const n = new Set();
            for (const k of prev) {
                const [e, s] = k.split("-");
                if (Number(e) !== exIdx) { n.add(k); continue; }
                if (Number(s) === setIdx) continue;
                if (Number(s) > setIdx) {
                    // Renumber down by one to follow the re-indexed set list
                    const parts = k.split("-");
                    parts[1] = String(Number(s) - 1);
                    n.add(parts.join("-"));
                } else {
                    n.add(k);
                }
            }
            return n;
        });
    };

    const save = useCallback(async ({ silent = false } = {}) => {
        if (!log) return false;
        try {
            await api.put(`/workouts/${logId}`, { exercises: log.exercises });
            if (!silent) toast.success("Saved");
            return true;
        } catch (e) {
            if (!silent) toast.error("Save failed");
            return false;
        }
    }, [log, logId]);

    // Debounced auto-save: every user edit marks the session dirty and schedules
    // a PUT 900ms after the last keystroke. The exercise log on the server is
    // always current, so users can navigate away and resume without losing work.
    useEffect(() => {
        if (!log) return;
        if (!dirtyRef.current) return;
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(async () => {
            const ok = await save({ silent: true });
            dirtyRef.current = false;
            setAutoSaveState(ok ? "saved" : "idle");
            if (ok) {
                // Fade the "Saved" pill back to "idle" after a moment
                setTimeout(() => setAutoSaveState((s) => (s === "saved" ? "idle" : s)), 1600);
            }
        }, 900);
        return () => {
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        };
    }, [log, save]);

    // Save once more on tab close so nothing in flight is lost. `sendBeacon`
    // can only POST, so we hit a dedicated POST alias of the autosave route.
    useEffect(() => {
        const handler = () => {
            if (!dirtyRef.current || !log) return;
            try {
                const token = localStorage.getItem("hg_token") || "";
                const url = `${api.defaults.baseURL || ""}/workouts/${logId}/autosave?token=${encodeURIComponent(token)}`;
                const blob = new Blob([JSON.stringify({ exercises: log.exercises })], { type: "application/json" });
                if (navigator.sendBeacon) navigator.sendBeacon(url, blob);
            } catch (e) { /* swallow */ }
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [log, logId]);

    // Validate every working set has weight > 0 and reps > 0 (and the same
    // for the left-side fields when unilateral). RIR is allowed to be 0
    // (means "trained to failure") — confirmed via RIRConfirmModal before
    // the session is logged. Returns the Set of "exIdx-setIdx-field" keys
    // that are still blank, plus the index of the first blank exercise.
    //
    // Optional exercises (those flagged `optional=True` on the source
    // exercise — e.g. shrugs, abs, rear delt fly, adductor, etc.) follow
    // an all-or-nothing rule: if EVERY field across EVERY set is blank/0,
    // the user has decided not to do the exercise and we skip validation
    // entirely. If ANY field has data, the exercise must be fully filled
    // — partial data usually means "I forgot a number", not "I skipped".
    const validateSets = useCallback(() => {
        const errs = new Set();
        let firstExIdx = null;
        (log?.exercises || []).forEach((ex, exIdx) => {
            // Detect whether the user touched this optional exercise at all
            // by checking if any field across any set has a positive value.
            if (ex.optional) {
                const hasAnyData = (ex.sets || []).some((s) => {
                    if (Number(s.weight) > 0 || Number(s.reps) > 0) return true;
                    if (ex.unilateral && (Number(s.weight_left) > 0 || Number(s.reps_left) > 0)) return true;
                    return false;
                });
                if (!hasAnyData) return; // fully blank → treated as "not done", skip validation
            }
            (ex.sets || []).forEach((s, setIdx) => {
                const checks = [["weight", s.weight], ["reps", s.reps]];
                if (ex.unilateral) {
                    checks.push(["weight_left", s.weight_left], ["reps_left", s.reps_left]);
                }
                for (const [field, val] of checks) {
                    if (!val || Number(val) <= 0) {
                        errs.add(`${exIdx}-${setIdx}-${field}`);
                        if (firstExIdx === null) firstExIdx = exIdx;
                    }
                }
            });
        });
        return { errs, firstExIdx };
    }, [log]);

    // Walk every working set and collect the ones the user marked as
    // failure (RIR === 0) and the ones that look under-trained (RIR >= 3).
    // Used to decide whether to fire the RIR confirm modal on Finish.
    const collectRIRFlags = useCallback(() => {
        const failureSets = [];
        const lowIntensitySets = [];
        (log?.exercises || []).forEach((ex) => {
            (ex.sets || []).forEach((s, setIdx) => {
                const setNumber = setIdx + 1;
                const push = (bucket, rir, side) => bucket.push({
                    exercise: ex.exercise_name || ex.name || "Exercise",
                    setNumber,
                    side,
                    rir,
                });
                if (ex.unilateral) {
                    if (Number(s.rir) === 0) push(failureSets, 0, "R");
                    else if (Number(s.rir) >= 3) push(lowIntensitySets, Number(s.rir), "R");
                    if (Number(s.rir_left) === 0) push(failureSets, 0, "L");
                    else if (Number(s.rir_left) >= 3) push(lowIntensitySets, Number(s.rir_left), "L");
                } else {
                    if (Number(s.rir) === 0) push(failureSets, 0);
                    else if (Number(s.rir) >= 3) push(lowIntensitySets, Number(s.rir));
                }
            });
        });
        return { failureSets, lowIntensitySets };
    }, [log]);

    // Per-exercise validation used by the Next/Skip button in focus mode.
    // Mirrors validateSets but scoped to a single index — returns the set
    // of blank-field keys for THIS exercise only. Optional + fully blank →
    // empty set (allowed to advance).
    const validateExerciseAt = useCallback((exIdx) => {
        const errs = new Set();
        const ex = log?.exercises?.[exIdx];
        if (!ex) return errs;
        if (ex.optional) {
            const hasAnyData = (ex.sets || []).some((s) => {
                if (Number(s.weight) > 0 || Number(s.reps) > 0) return true;
                if (ex.unilateral && (Number(s.weight_left) > 0 || Number(s.reps_left) > 0)) return true;
                return false;
            });
            if (!hasAnyData) return errs;
        }
        (ex.sets || []).forEach((s, setIdx) => {
            const checks = [["weight", s.weight], ["reps", s.reps]];
            if (ex.unilateral) checks.push(["weight_left", s.weight_left], ["reps_left", s.reps_left]);
            for (const [field, val] of checks) {
                if (!val || Number(val) <= 0) errs.add(`${exIdx}-${setIdx}-${field}`);
            }
        });
        return errs;
    }, [log]);

    // True only when an optional exercise has every field blank — used to
    // flip the button label from "Next" to "Skip" so the user can tell.
    const isCurrentSkippable = useMemo(() => {
        const ex = log?.exercises?.[focusIdx];
        if (!ex?.optional) return false;
        return !(ex.sets || []).some((s) => {
            if (Number(s.weight) > 0 || Number(s.reps) > 0) return true;
            if (ex.unilateral && (Number(s.weight_left) > 0 || Number(s.reps_left) > 0)) return true;
            return false;
        });
    }, [log, focusIdx]);

    // Smooth-scroll the session progress strip + current exercise card into
    // view on every advance. Offset accounts for the sticky 64px navbar plus
    // a hair of breathing room. Wrapped in rAF so React has time to render
    // the new pane (key change → remount) before we measure.
    const scrollFocusIntoView = useCallback(() => {
        requestAnimationFrame(() => {
            const target = document.querySelector('[data-testid="session-progress"]');
            if (!target) return;
            const top = target.getBoundingClientRect().top + window.scrollY - 80;
            window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
        });
    }, []);

    const goPrev = () => {
        if (focusIdx <= 0) return;
        setSlideDir("prev");
        setFocusIdx(focusIdx - 1);
        setPunchKey((k) => k + 1);
        scrollFocusIntoView();
    };

    const goNext = () => {
        const errs = validateExerciseAt(focusIdx);
        if (errs.size > 0) {
            // Merge into the global invalidFields set so blank inputs go red,
            // then trigger the shake animation on the card.
            setInvalidFields((prev) => {
                const next = new Set(prev);
                errs.forEach((k) => next.add(k));
                return next;
            });
            setShakeKey((k) => k + 1);
            toast.error(`${errs.size} field${errs.size === 1 ? "" : "s"} blank — fill them or skip.`);
            return;
        }
        // Clear any errors for this exercise as we advance.
        setInvalidFields((prev) => {
            const next = new Set();
            prev.forEach((k) => { if (!k.startsWith(`${focusIdx}-`)) next.add(k); });
            return next;
        });
        if (focusIdx >= (log?.exercises?.length || 1) - 1) {
            // Last exercise → trigger the same finish flow as the global CTA.
            setPunchKey((k) => k + 1);
            finish();
            return;
        }
        setSlideDir("next");
        setFocusIdx(focusIdx + 1);
        setPunchKey((k) => k + 1);
        scrollFocusIntoView();
    };

    const finish = async () => {
        const { errs, firstExIdx } = validateSets();
        if (errs.size > 0) {
            setInvalidFields(errs);
            toast.error(`${errs.size} field${errs.size === 1 ? "" : "s"} blank · weight and reps are required to log a workout.`);
            // Jump focus mode back to the first incomplete exercise so the
            // user lands on the offending card without scrolling.
            if (firstExIdx !== null && !isReadOnly) {
                setSlideDir(firstExIdx < focusIdx ? "prev" : "next");
                setFocusIdx(firstExIdx);
                setShakeKey((k) => k + 1);
                scrollFocusIntoView();
            } else if (firstExIdx !== null) {
                const el = document.querySelector(`[data-testid="exercise-card-${firstExIdx}"]`);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            return;
        }
        setInvalidFields(new Set());
        // Surface unusual RIR values (0 = failure, >=3 = under-trained) for
        // explicit confirmation before we commit the workout. If either
        // bucket is non-empty, hand control to the RIRConfirmModal.
        const flags = collectRIRFlags();
        if (flags.failureSets.length > 0 || flags.lowIntensitySets.length > 0) {
            setRirFlags(flags);
            setRirConfirmOpen(true);
            return;
        }
        await doFinish();
    };

    const doFinish = async () => {
        try {
            await api.put(`/workouts/${logId}`, { exercises: log.exercises });
            const { data } = await api.post(`/workouts/${logId}/finish`, { perceived_effort: effort });
            const earned = data.exp_earned ?? 0;
            setExpEarned(earned);
            const prs = data.prs_set || [];
            setPrsSet(prs);
            const hasRank = !!data.rank_up;
            const hasPRs = prs.length > 0;
            if (hasRank) {
                setRankUp(data.rank_up);
                if (hasPRs) {
                    toast.success(`+${earned} EXP · Rank up + ${prs.length} PR${prs.length > 1 ? 's' : ''}!`);
                } else {
                    toast.success(`+${earned} EXP · Rank up!`);
                }
            } else if (hasPRs) {
                setShowPRs(true);
                toast.success(`+${earned} EXP · ${prs.length} PR${prs.length > 1 ? 's' : ''} crushed`);
            } else {
                toast.success(`Session complete · +${earned} EXP`);
                navigate("/dashboard");
            }
        } catch (e) {
            toast.error("Could not finish");
        }
    };

    const handleCelebrationClose = () => {
        setRankUp(null);
        if (prsSet.length > 0) {
            setShowPRs(true);
        } else {
            navigate("/dashboard");
        }
    };

    const handlePRsClose = () => {
        setShowPRs(false);
        navigate("/dashboard");
    };

    if (!log) return <div data-testid="session-loading">Loading…</div>;

    return (
        <div className="space-y-6 pb-32" data-testid="session-page">
            <div>
                <div className="label-eyebrow text-accent-red">Active session</div>
                <h1 className="font-display text-4xl font-black uppercase sm:text-5xl">{log.day_name}</h1>
                <p className="mt-2 text-xs uppercase tracking-wider text-muted-foreground">Log reps × weight × RIR. 1–2 RIR target. Max {MAX_SETS} sets/exercise.</p>
            </div>

            <div className="space-y-4">
                {/* Focus-mode progress header. Only renders during an active
                    session; read-only mode shows the full exercise list. */}
                {!isReadOnly && log.exercises.length > 0 && (
                    <div className="flex items-center gap-3" data-testid="session-progress">
                        <div className="label-eyebrow flex-none text-accent-red">
                            <span data-testid="session-progress-label">Exercise {focusIdx + 1} / {log.exercises.length}</span>
                        </div>
                        <div className="flex-1 h-1.5 overflow-hidden bg-white/10">
                            <div
                                className="session-progress-fill h-full bg-accent-red"
                                style={{ width: `${((focusIdx + 1) / log.exercises.length) * 100}%` }}
                            />
                        </div>
                        <div className="hidden truncate text-[10px] uppercase tracking-wider text-muted-foreground sm:block sm:max-w-[280px]">
                            {log.exercises[focusIdx]?.exercise_name}
                        </div>
                    </div>
                )}

                {/* Slide-animation wrapper. The `key` on the outer div forces
                    React to re-mount when focusIdx changes, replaying the
                    `.session-pane` slide-in CSS keyframe. Direction is set
                    by slideDir (next = from right, prev = from left). */}
                <div
                    key={isReadOnly ? "readonly" : `focus-${focusIdx}-${shakeKey}`}
                    className={!isReadOnly ? `session-slide-${slideDir}${shakeKey > 0 ? " session-shake" : ""}` : ""}
                    data-testid={!isReadOnly ? `focus-pane-${focusIdx}` : "readonly-pane"}
                >
                <div className={!isReadOnly ? "session-pane" : ""}>
                {log.exercises
                    .map((ex, i) => ({ ex, i }))
                    .filter(({ i }) => isReadOnly || i === focusIdx)
                    .map(({ ex, i: exIdx }) => {
                    const flagged = ex.unilateral ? imbalanceMap[ex.exercise_id] : null;
                    return (
                    <div key={exIdx} className="tactical-card p-5" data-testid={`exercise-card-${exIdx}`}>
                        {flagged && (
                            <div className="mb-4 flex items-start gap-3 border border-accent-red bg-accent-red/10 p-3" data-testid={`imbalance-nudge-${exIdx}`}>
                                <Scale className="mt-0.5 h-4 w-4 flex-none text-accent-red" />
                                <div className="text-xs leading-relaxed text-foreground/90">
                                    <span className="label-eyebrow text-accent-red">Side gap · {flagged.gap_pct}%</span>
                                    <p className="mt-1">
                                        Your <span className="font-bold uppercase text-accent-red">{flagged.lagging_side}</span> side is behind on {ex.exercise_name}. Start with the <span className="font-bold uppercase">{flagged.lagging_side}</span> side this session and let it set the pace — match its reps on the stronger side.
                                    </p>
                                </div>
                            </div>
                        )}
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                                <div className="label-eyebrow text-muted-foreground">{ex.slot_label}</div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <h3 className={`font-display text-xl font-black uppercase ${!isReadOnly ? "session-name-pop" : ""}`} data-testid={`exercise-name-${exIdx}`}>{ex.exercise_name}</h3>
                                    {ex.optional && (
                                        <span
                                            data-testid={`optional-badge-${exIdx}`}
                                            className="rounded-sm border border-amber-400/60 bg-amber-400/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-400"
                                        >
                                            Optional · leave blank to skip
                                        </span>
                                    )}
                                </div>
                                <span className="label-eyebrow text-muted-foreground">{ex.is_compound ? "Compound · 3 sets default" : "Isolation · 2 sets default"}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                {!isReadOnly && (
                                    <>
                                        <Link
                                            to={`/exercises/${ex.exercise_id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`form-guide-${exIdx}`}
                                            className="flex items-center gap-1 rounded-sm border border-border px-3 py-1 text-xs font-bold uppercase tracking-wider hover:bg-secondary"
                                        >
                                            <BookOpen className="h-3 w-3" /> Form guide
                                        </Link>
                                        <button
                                            type="button"
                                            onClick={() => toggleDemo(exIdx)}
                                            data-testid={`form-check-toggle-${exIdx}`}
                                            className="flex items-center gap-1 rounded-sm border border-border px-3 py-1 text-xs font-bold uppercase tracking-wider hover:bg-secondary"
                                        >
                                            {openDemos[exIdx] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                                            {openDemos[exIdx] ? "Hide demo" : "Form check"}
                                        </button>
                                        <button
                                            onClick={() => addSet(exIdx)}
                                            disabled={ex.sets.length >= MAX_SETS}
                                            data-testid={`add-set-${exIdx}`}
                                            className="flex items-center gap-1 rounded-sm border border-border px-3 py-1 text-xs font-bold uppercase tracking-wider hover:bg-secondary disabled:opacity-40"
                                        >
                                            <Plus className="h-3 w-3" /> Set ({ex.sets.length}/{MAX_SETS})
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="mt-4 overflow-x-auto">
                            {openDemos[exIdx] && (
                                <div className="mb-4 grid gap-3 sm:grid-cols-[1.6fr_1fr]" data-testid={`form-check-panel-${exIdx}`}>
                                    <ExerciseDemo
                                        frames={ex.demo_frames || []}
                                        youtubeId={ex.youtube_id || ""}
                                        youtubeId2={ex.youtube_id_2 || ""}
                                        name={ex.exercise_name}
                                    />
                                    <MuscleMap
                                        primary={ex.target_muscle || ex.muscle_group}
                                        pattern={ex.pattern}
                                    />
                                </div>
                            )}
                            {Array.isArray(ex.warmup_sets) && ex.warmup_sets.length > 0 && (
                                <div className="mb-4 border-l-2 border-accent-red/50 bg-secondary/40 px-4 py-3" data-testid={`warmups-${exIdx}`}>
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <div className="flex items-center gap-2 label-eyebrow text-accent-red">
                                            <Flame className="h-3.5 w-3.5" />
                                            Warm-up · {ex.warmup_sets.length} set{ex.warmup_sets.length > 1 ? "s" : ""}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => refillWarmups(exIdx)}
                                            data-testid={`warmup-refill-${exIdx}`}
                                            className="rounded-sm border border-border px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground hover:border-accent-red hover:text-accent-red"
                                        >
                                            Use suggested
                                        </button>
                                    </div>
                                    <table className="mt-2 w-full min-w-[420px] text-sm">
                                        <thead>
                                            <tr className="label-eyebrow text-muted-foreground">
                                                <th className="pb-1 text-left w-10">#</th>
                                                <th className="pb-1 text-left">Weight</th>
                                                <th className="pb-1 text-left">Reps</th>
                                                <th className="pb-1 text-left">Target</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {ex.warmup_sets.map((w, wIdx) => (
                                                <tr key={wIdx} className="border-t border-border/60" data-testid={`warmup-row-${exIdx}-${wIdx}`}>
                                                    <td className="py-1.5 text-xs font-bold text-muted-foreground">W{wIdx + 1}</td>
                                                    <td className="py-1.5">
                                                        <NumInput
                                                            value={w.weight}
                                                            step={0.5}
                                                            onChange={(v) => updateWarmup(exIdx, wIdx, v)}
                                                            testId={`warmup-weight-${exIdx}-${wIdx}`}
                                                            className="w-20"
                                                        />
                                                    </td>
                                                    <td className="py-1.5 text-sm tabular-nums">{w.reps}</td>
                                                    <td className="py-1.5 text-xs text-muted-foreground">{w.target_pct}% × {w.reps}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            <div className="mb-2 flex items-center gap-2 label-eyebrow text-muted-foreground" data-testid={`working-sets-label-${exIdx}`}>
                                Working sets · {ex.is_compound ? "compound" : "isolation"}
                            </div>
                            {!isReadOnly && (
                                <LastTimeChip
                                    sessions={lastTimeMap[ex.exercise_id]}
                                    unilateral={ex.unilateral}
                                    testIdPrefix={`last-time-${exIdx}`}
                                />
                            )}
                            <table className="w-full text-sm" data-testid={`working-sets-${exIdx}`}>
                                {ex.unilateral ? (
                                    <>
                                        <thead>
                                            <tr className="label-eyebrow text-muted-foreground">
                                                <th className="pb-2 text-left w-10">#</th>
                                                <th className="pb-2 text-center w-8 text-accent-red">L</th>
                                                <th className="pb-2 text-left">Weight</th>
                                                <th className="pb-2 text-left">Reps</th>
                                                <th className="pb-2 text-left">RIR</th>
                                                <th className="pb-2 text-center w-8 text-accent-red">R</th>
                                                <th className="pb-2 text-left">Weight</th>
                                                <th className="pb-2 text-left">Reps</th>
                                                <th className="pb-2 text-left">RIR</th>
                                                <th />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {ex.sets.map((s, setIdx) => (
                                                <tr key={setIdx} className="border-t border-border" data-testid={`uni-set-${exIdx}-${setIdx}`}>
                                                    <td className="py-2 font-bold">{s.set_number}</td>
                                                    <td className="py-2 text-center text-xs font-bold text-accent-red">L</td>
                                                    <td className="py-2"><NumInput value={s.weight_left} step={0.5} onChange={(v) => updateSet(exIdx, setIdx, "weight_left", v)} testId={`weight-left-${exIdx}-${setIdx}`} invalid={invalidFields.has(`${exIdx}-${setIdx}-weight_left`)} /></td>
                                                    <td className="py-2"><NumInput value={s.reps_left} step={1} onChange={(v) => updateSet(exIdx, setIdx, "reps_left", v)} testId={`reps-left-${exIdx}-${setIdx}`} invalid={invalidFields.has(`${exIdx}-${setIdx}-reps_left`)} /></td>
                                                    <td className="py-2"><NumInput value={s.rir_left} step={1} min={0} max={5} onChange={(v) => updateSet(exIdx, setIdx, "rir_left", v)} testId={`rir-left-${exIdx}-${setIdx}`} className="w-16" invalid={invalidFields.has(`${exIdx}-${setIdx}-rir_left`)} showZero /></td>
                                                    <td className="py-2 text-center text-xs font-bold text-accent-red">R</td>
                                                    <td className="py-2"><NumInput value={s.weight} step={0.5} onChange={(v) => updateSet(exIdx, setIdx, "weight", v)} testId={`weight-right-${exIdx}-${setIdx}`} invalid={invalidFields.has(`${exIdx}-${setIdx}-weight`)} /></td>
                                                    <td className="py-2"><NumInput value={s.reps} step={1} onChange={(v) => updateSet(exIdx, setIdx, "reps", v)} testId={`reps-right-${exIdx}-${setIdx}`} invalid={invalidFields.has(`${exIdx}-${setIdx}-reps`)} /></td>
                                                    <td className="py-2"><NumInput value={s.rir} step={1} min={0} max={5} onChange={(v) => updateSet(exIdx, setIdx, "rir", v)} testId={`rir-right-${exIdx}-${setIdx}`} className="w-16" invalid={invalidFields.has(`${exIdx}-${setIdx}-rir`)} /></td>
                                                    <td className="py-2 text-right"><button onClick={() => removeSet(exIdx, setIdx)} className="text-muted-foreground hover:text-accent-red" data-testid={`remove-set-${exIdx}-${setIdx}`}><Trash2 className="h-4 w-4" /></button></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </>
                                ) : (
                                    <>
                                        <thead>
                                            <tr className="label-eyebrow text-muted-foreground">
                                                <th className="pb-2 text-left">#</th>
                                                <th className="pb-2 text-left">Weight</th>
                                                <th className="pb-2 text-left">Reps</th>
                                                <th className="pb-2 text-left">RIR</th>
                                                <th />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {ex.sets.map((s, setIdx) => (
                                                <tr key={setIdx} className="border-t border-border">
                                                    <td className="py-2 font-bold">{s.set_number}</td>
                                                    <td className="py-2"><NumInput value={s.weight} step={0.5} onChange={(v) => updateSet(exIdx, setIdx, "weight", v)} testId={`weight-${exIdx}-${setIdx}`} invalid={invalidFields.has(`${exIdx}-${setIdx}-weight`)} /></td>
                                                    <td className="py-2"><NumInput value={s.reps} step={1} onChange={(v) => updateSet(exIdx, setIdx, "reps", v)} testId={`reps-${exIdx}-${setIdx}`} invalid={invalidFields.has(`${exIdx}-${setIdx}-reps`)} /></td>
                                                    <td className="py-2"><NumInput value={s.rir} step={1} min={0} max={5} onChange={(v) => updateSet(exIdx, setIdx, "rir", v)} testId={`rir-${exIdx}-${setIdx}`} className="w-16" invalid={invalidFields.has(`${exIdx}-${setIdx}-rir`)} showZero /></td>
                                                    <td className="py-2 text-right"><button onClick={() => removeSet(exIdx, setIdx)} className="text-muted-foreground hover:text-accent-red" data-testid={`remove-set-${exIdx}-${setIdx}`}><Trash2 className="h-4 w-4" /></button></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </>
                                )}
                            </table>
                            {ex.unilateral && (
                                <p className="mt-2 text-[11px] uppercase tracking-widest text-accent-red/80" data-testid={`uni-note-${exIdx}`}>
                                    Unilateral · log left and right separately · match reps across sides
                                </p>
                            )}
                        </div>
                    </div>
                    );
                })}
                </div>
                </div>
            </div>

            {/* Nav sits IMMEDIATELY under the exercise card so the Prev/Next
                buttons feel anchored to it. Effort + autosave + read-only
                banner all render below the nav. */}
            {!isReadOnly && (
                <div className="flex gap-2 sm:gap-3" data-testid="session-nav">
                    {/* Previous — disabled on the first exercise, active otherwise. */}
                    <button
                        onClick={goPrev}
                        disabled={focusIdx === 0}
                        data-testid="session-prev"
                        className="flex items-center justify-center gap-1.5 rounded-sm border border-border bg-card/90 px-4 py-3 text-xs font-bold uppercase tracking-wider backdrop-blur transition-all active:scale-95 hover:bg-secondary disabled:opacity-30 disabled:hover:bg-card/90 sm:text-sm"
                    >
                        <ChevronLeft className="h-4 w-4" />
                        <span className="hidden sm:inline">Previous</span>
                    </button>

                    {/* Save Now — kept as a tertiary action so users can force
                        a sync. Auto-save fires on every change anyway, so this
                        is mostly belt-and-suspenders for trust. */}
                    <button
                        onClick={() => save({ silent: false })}
                        data-testid="save-session"
                        className="flex items-center justify-center gap-1.5 rounded-sm border border-border bg-card/90 px-3 py-3 text-xs font-bold uppercase tracking-wider backdrop-blur transition-all active:scale-95 hover:bg-secondary sm:text-sm"
                    >
                        <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                        <span className="hidden sm:inline">Save</span>
                    </button>

                    {/* The hero CTA. Morphs between Next / Skip / Finish based
                        on context. `key={punchKey}` forces React to re-mount
                        on every press so the punch-ring animation plays fresh
                        — combined with active:scale, it gives a tactile thump. */}
                    <button
                        key={`cta-${punchKey}`}
                        onClick={goNext}
                        data-testid={focusIdx === log.exercises.length - 1 ? "finish-session" : "session-next"}
                        className="session-cta-punch flex flex-1 items-center justify-center gap-1.5 rounded-sm bg-accent-red px-4 py-3 text-sm font-bold uppercase tracking-wider text-white shadow-[0_4px_24px_-8px_rgba(239,68,68,0.5)] transition-all active:scale-[0.97] hover:bg-[color:var(--accent-hover)]"
                    >
                        {focusIdx === log.exercises.length - 1 ? (
                            <>
                                <Zap className="h-4 w-4" />
                                Finish session
                            </>
                        ) : isCurrentSkippable ? (
                            <>
                                <SkipForward className="h-4 w-4" />
                                Skip exercise
                                <ChevronRight className="h-4 w-4" />
                            </>
                        ) : (
                            <>
                                Next exercise
                                <ChevronRight className="h-4 w-4" />
                            </>
                        )}
                    </button>
                </div>
            )}

            {isReadOnly && (
                <div className="rounded-sm border border-accent-red/40 bg-accent-red/5 p-4" data-testid="readonly-banner">
                    <div className="label-eyebrow text-accent-red">Previous workout · read-only</div>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Finished {log?.finished_at ? new Date(log.finished_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : ""}. Fields are locked — reviewing a completed session.
                    </p>
                </div>
            )}

            {/* Session effort lives at the end of the workout, not under every
                exercise. We render it only on the last exercise (so the user
                rates the entire session right before they hit Finish) and in
                read-only mode (so the previous score is reviewable). */}
            {(isReadOnly || focusIdx === log.exercises.length - 1) && (
                <div className="tactical-card p-5" data-testid="session-effort-card">
                    <div className="label-eyebrow text-accent-red">Session effort (1–10){isReadOnly ? " · locked" : ""}</div>
                    <input
                        type="range"
                        min="1"
                        max="10"
                        value={effort}
                        onChange={e => !isReadOnly && setEffort(parseInt(e.target.value))}
                        disabled={isReadOnly}
                        data-testid="effort-slider"
                        className="mt-3 w-full accent-[#ff3b30] disabled:cursor-not-allowed disabled:opacity-70"
                    />
                    <div className="font-display text-4xl font-black">{effort}/10</div>
                    {!isReadOnly && effort >= 9 && <p className="mt-2 text-xs text-amber-500">Very high. Consider reducing a set or dropping load 5% next session.</p>}
                </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3 rounded-sm border border-border bg-secondary/30 px-3 py-2 text-xs" data-testid="autosave-status">
                <div className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle2 className={`h-3.5 w-3.5 ${autoSaveState === "saving" ? "animate-pulse text-amber-400" : "text-emerald-400"}`} />
                    <span>
                        {isReadOnly ? "Read-only · previous workout"
                            : autoSaveState === "saving" ? "Saving…"
                            : autoSaveState === "saved" ? "Saved"
                            : "Auto-saving on every change · resume anytime."}
                    </span>
                </div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {isReadOnly ? "Fields locked" : "Weight & reps required. RIR 0 = trained to failure. Optional exercises may be left blank."}
                </div>
            </div>

            <RestTimer />
            <RankUpModal rankUp={rankUp} expEarned={expEarned} onClose={handleCelebrationClose} />
            {showPRs && <PRModal prs={prsSet} onClose={handlePRsClose} /> }
            <RIRConfirmModal
                open={rirConfirmOpen}
                failureSets={rirFlags.failureSets}
                lowIntensitySets={rirFlags.lowIntensitySets}
                onCancel={() => setRirConfirmOpen(false)}
                onConfirm={async () => {
                    setRirConfirmOpen(false);
                    await doFinish();
                }}
            />
        </div>
    );
}
